import React, { useState } from "react";
import { X, Plus, GripVertical } from "lucide-react";
import { Character } from "./types";
import { StatCard } from "./StatCard";

interface FeaturesTabProps {
	character: Character;
	updateCharacter: (path: string, value: any) => void;
	addItem: (category: keyof Character, item: any) => void;
	removeItem: (category: keyof Character, index: number) => void;
}

export const FeaturesTab: React.FC<FeaturesTabProps> = ({
	character,
	updateCharacter,
	addItem,
	removeItem,
}) => {
	const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

	const handleDragStart = (index: number) => {
		setDraggedIndex(index);
	};

	const handleDragOver = (e: React.DragEvent, index: number) => {
		e.preventDefault();
		if (draggedIndex === null || draggedIndex === index) return;

		const newFeatures = [...character.features];
		const draggedItem = newFeatures[draggedIndex];
		newFeatures.splice(draggedIndex, 1);
		newFeatures.splice(index, 0, draggedItem);

		updateCharacter("features", newFeatures);
		setDraggedIndex(index);
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
	};

	const autoResize = (textarea: HTMLTextAreaElement) => {
		textarea.style.height = "auto";
		textarea.style.height = textarea.scrollHeight + "px";
	};

	return (
		<div className="space-y-8">
			{/* Features & Traits */}
			<StatCard title="Features & Traits">
				<div className="space-y-1">
					{character.features.map((feature, index) => (
						<div
							key={index}
							className={`flex gap-3 ${draggedIndex === index ? "opacity-50" : ""}`}
							draggable
							onDragStart={() => handleDragStart(index)}
							onDragOver={(e) => handleDragOver(e, index)}
							onDragEnd={handleDragEnd}
						>
							<div className="flex items-start pt-3 cursor-grab active:cursor-grabbing">
								<GripVertical
									size={20}
									className="text-gray-400"
								/>
							</div>
							<textarea
								value={feature}
								onChange={(e) => {
									const newFeatures = [...character.features];
									newFeatures[index] = e.target.value;
									updateCharacter("features", newFeatures);
									autoResize(e.target);
								}}
								onInput={(e) =>
									autoResize(e.target as HTMLTextAreaElement)
								}
								ref={(el) => {
									if (el) {
										autoResize(el);
									}
								}}
								className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 resize-none overflow-hidden"
								rows={1}
								placeholder="Feature name and description"
							/>
							<button
								onClick={() => removeItem("features", index)}
								className="p-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 self-start"
							>
								<X size={16} />
							</button>
						</div>
					))}
					<button
						onClick={() => addItem("features", "")}
						className="flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors duration-200 font-semibold"
					>
						<Plus size={16} />
						Add Feature
					</button>
				</div>
			</StatCard>

			{/* Character Traits */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<StatCard title="Personality Traits">
					<textarea
						value={character.personalityTraits}
						onChange={(e) =>
							updateCharacter("personalityTraits", e.target.value)
						}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 resize-none"
						rows={4}
						placeholder="Describe your character's personality..."
					/>
				</StatCard>

				<StatCard title="Ideals">
					<textarea
						value={character.ideals}
						onChange={(e) =>
							updateCharacter("ideals", e.target.value)
						}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 resize-none"
						rows={4}
						placeholder="What drives your character..."
					/>
				</StatCard>

				<StatCard title="Bonds">
					<textarea
						value={character.bonds}
						onChange={(e) =>
							updateCharacter("bonds", e.target.value)
						}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 resize-none"
						rows={4}
						placeholder="Important connections and relationships..."
					/>
				</StatCard>

				<StatCard title="Flaws">
					<textarea
						value={character.flaws}
						onChange={(e) =>
							updateCharacter("flaws", e.target.value)
						}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 resize-none"
						rows={4}
						placeholder="Character weaknesses and vices..."
					/>
				</StatCard>
			</div>
		</div>
	);
};
